import React, { useEffect, useRef } from "react";
import logo from "./assets/logofinal-flat.png";
import "./HomeV2.css";
import "./UseCaseTemplate.css";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";

const USE_CASE_LINKS = [
  ["Booking", "/use-cases/booking"],
  ["Hotel", "/use-cases/hotel"],
  ["Real Estate", "/use-cases/real-estate"],
  ["Museum", "/use-cases/museum"],
  ["Live Events", "/use-cases/live-events"],
  ["Sport", "/use-cases/sport"],
  ["Media", "/use-cases/media"],
  ["AI Proposal", "/use-cases/ai-proposal"],
  ["Geolocalisation", "/use-cases/geolocalisation"],
  ["Map", "/use-cases/map"],
  ["Wizard", "/use-cases/wizard"],
];

function MediaPanel({ item }) {
  const videoRef = useRef(null);

  const playPreview = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    const playPromise = video.play();
    if (playPromise?.catch) playPromise.catch(() => {});
  };

  const pausePreview = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    const playPromise = video.play();
    if (playPromise?.catch) playPromise.catch(() => {});
  };

  return (
    <div className={`ucp-mediaGrid ${item.videoPortrait ? "ucp-mediaGridPortrait" : ""}`}>
      <figure className="ucp-imageCard">
        <img src={item.image} alt={`${item.title} Weavevo concept`} loading="lazy" />
      </figure>

      {item.video ? (
        <div
          className={`ucp-videoCard ${item.videoPortrait ? "is-portrait" : ""}`}
          onMouseEnter={playPreview}
          onMouseLeave={pausePreview}
        >
          <video
            ref={videoRef}
            controls
            autoPlay
            muted
            loop
            preload="auto"
            playsInline
            poster={item.image}
            onMouseEnter={playPreview}
            onMouseLeave={pausePreview}
          >
            <source src={item.video} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      ) : null}
    </div>
  );
}

function ExperienceSection({ item, index }) {
  const sectionClassName = [
    "ucp-experience",
    index % 2 ? "is-reversed" : "",
    item.sideTitle ? "has-side-title" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const sectionHeading = (
    <>
      <div className="ucp-kickerRow">
        <span className="ucp-number">{item.number}</span>
        <span className="ucp-category">{item.category}</span>
      </div>
      <h2>{item.title}</h2>
    </>
  );

  return (
    <section className={sectionClassName} id={item.id}>
      {!item.sideTitle ? <div className="ucp-experienceHeader">{sectionHeading}</div> : null}

      <div className="ucp-experienceCopy">
        {item.sideTitle ? <div className="ucp-sideTitle">{sectionHeading}</div> : null}
        {item.lead ? <p className="ucp-lead">{item.lead}</p> : null}

        {item.problem ? (
          <div className="ucp-problemCard">
            <span>{item.problemLabel || "Problem solved"}</span>
            <p>{item.problem}</p>
          </div>
        ) : null}

        {item.layout ? (
          <div className="ucp-layoutBadge">
            <div>
              <small>Weavevo layout</small>
              <strong>{item.layout}</strong>
            </div>
            <p>{item.layoutDetail}</p>
          </div>
        ) : null}

        {item.workflow?.length ? (
          <div className="ucp-workflow">
            <h3>{item.workflowTitle || "Workflow"}</h3>
            <ol>
              {item.workflow.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </div>
        ) : null}

        {item.outcome1 || item.outcome2 ? (
          <div className="ucp-outcomes" aria-label="Use-case outcomes">
            {item.outcome1 ? (
              <article>
                <span>{item.outcome1Label || "Business impact"}</span>
                <p>{item.outcome1}</p>
              </article>
            ) : null}
            {item.outcome2 ? (
              <article>
                <span>{item.outcome2Label || "Weaver monetization"}</span>
                <p>{item.outcome2}</p>
              </article>
            ) : null}
          </div>
        ) : null}
      </div>

      <MediaPanel item={item} />
    </section>
  );
}

export default function UseCaseTemplate({
  pageTitle,
  currentPath,
  eyebrow,
  heroTitle,
  heroText,
  heroImage,
  heroAlt,
  experiences,
  finalEyebrow,
  finalTitle,
  finalText
}) {
  useEffect(() => {
    document.title = `${pageTitle} | Weavevo`;
    window.scrollTo(0, 0);
  }, [pageTitle]);

  return (
    <div className="ucp-page">
      <header className="hv2-nav hv2-navPersistent ucp-header" aria-label="Weavevo navigation">
        <a className="hv2-brand" href="/" aria-label="Weavevo home">
          <img src={logo} alt="Weavevo" />
          <span>weavevo</span>
        </a>
        <nav className="hv2-topMenu hv2-topMenuExpanded" aria-label="Home page sections">
          <a href="/homeV2#product">Product</a>
          <a href="/homeV2#highlights">Features</a>
          <a href="/homeV2#use-cases">Use Cases</a>
          <a href="/homeV2#technology">Technology</a>
          <a href="/homeV2#weavers">Weavers</a>
          <a href="/homeV2#footer-resources">Resources</a>
          <a href="/homeV2#footer-company">Company</a>
          <a className="hv2-headerLoginLink" href="/homeV2#product">Log in</a>
        </nav>
      </header>

      <main>
        <section className="ucp-hero">
          <img className="ucp-heroImage" src={heroImage} alt={heroAlt || `${pageTitle} Weavevo concept`} />
          <div className="ucp-heroScrim" />
          <div className="ucp-heroContent">
            <span className="ucp-eyebrow">{eyebrow}</span>
            <h1>{heroTitle}</h1>
            <p>{heroText}</p>
          </div>
        </section>

        <div className="ucp-experienceList">
          {experiences.map((item, index) => (
            <ExperienceSection item={item} index={index} key={item.id} />
          ))}
        </div>

        <section className="ucp-finalCta">
          <span className="ucp-sectionEyebrow">{finalEyebrow}</span>
          <h2>{finalTitle}</h2>
          <p>{finalText}</p>
          <div className="ucp-finalDownloadActions" aria-label="Download Weavevo">
            <a
              className="ucp-storeButton ucp-storeButtonPrimary"
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Open Weavevo on the Play Store"
            >
              <i className="bi bi-google-play" aria-hidden="true" />
              <span>Play Store</span>
            </a>
            <button
              className="ucp-storeButton ucp-storeButtonSecondary"
              type="button"
              disabled
              aria-label="Weavevo App Store button"
              title="App Store"
            >
              <i className="bi bi-apple" aria-hidden="true" />
              <span>App Store</span>
            </button>
          </div>
        </section>
      </main>

      <footer className="hv2-footerV2 hv2-footerV2Mega ucp-footer" id="site-footer" aria-label="Weavevo footer">
        <div className="hv2-footerMegaGrid">
          <div className="hv2-footerBrandColumn">
            <a className="hv2-footerBrand" href="/homeV2" aria-label="Weavevo home">
              <img src={logo} alt="" aria-hidden="true" />
              <span>weavevo</span>
            </a>
            <p>Living media built from connected human perspectives, intelligent curation and evolving video experiences.</p>
            <a className="hv2-footerContact" href="/contact">Contact / Support</a>
          </div>

          <div className="hv2-footerColumn">
            <h4>Product</h4>
            <a href="/homeV2#product">Overview</a>
            <a href="/homeV2#highlights">Features</a>
            <a href="/homeV2#technology">Technology</a>
            <a href="/homeV2#weavers">Weaver Network</a>
          </div>

          <div className="hv2-footerColumn" data-usecases>
            <h4>Use Cases</h4>
            {USE_CASE_LINKS.map(([label, href]) => (
              <a href={href} key={href} aria-current={href === currentPath ? "page" : undefined}>{label}</a>
            ))}
          </div>

          <div className="hv2-footerColumn" id="footer-resources">
            <h4>Resources</h4>
            <a href="/living-weave-pipeline">Living Weave Pipeline</a>
            <a href="/earning-model">Earning model</a>
            <a href="/homeV2#highlights">AI & Automation</a>
            <a href="/homeV2#weavers">For Weavers</a>
            <a href="/faq">Help & Support</a>
          </div>

          <div className="hv2-footerColumn" id="footer-company">
            <h4>Company</h4>
            <a href="/about">About Weavevo</a>
            <a href="/mentions-legales">Legal notice</a>
            <a href="/politique-confidentialite">Privacy policy</a>
            <a href="/cgu">Terms of use</a>
            <a href="/accessibilite-rgpd">Accessibility & GDPR</a>
          </div>
        </div>

        <div className="hv2-footerBottomLine hv2-footerMegaBottom">
          <span>© 2026 Weavevo. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
